import { useId, useState } from 'react'
import { Button } from './Button'
import { searchTours, type Tour } from '../tours'
import { Dialog } from './Dialog'
import { Icon } from './Icon'

export interface HelpDialogProps {
  isOpen: boolean
  onClose: () => void
  reportCount: number
  onOpenGuide: () => void
  /** Plays a visual guide. */
  onStartTour: (tour: Tour) => void
}

export function HelpDialog({
  isOpen,
  onClose,
  reportCount,
  onOpenGuide,
  onStartTour,
}: HelpDialogProps) {
  const [query, setQuery] = useState('')
  const searchId = useId()
  const tours = searchTours(query)

  return (
    <Dialog open={isOpen} onClose={onClose} labelledBy="help-dialog-title" className="help-dialog">
      <div className="help-dialog-header">
        <div className="help-dialog-title-group">
          <h2 id="help-dialog-title" className="help-dialog-title">
            Help
          </h2>
          <p className="help-dialog-subtitle">Let a guide show you where to click.</p>
        </div>
        <Button
          tone="quiet"
          size="small"
          icon="close"
          iconOnly
          label="Close help"
          className="help-dialog-close"
          onClick={onClose}
        />
      </div>

      <div className="help-dialog-body">
        <label htmlFor={searchId} className="visually-hidden">
          Find a guide
        </label>
        <input
          id={searchId}
          type="search"
          className="help-search"
          placeholder="Find a guide, e.g. compare, export, dark"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <section className="help-category-section" aria-labelledby="help-tours-title">
          <h3 id="help-tours-title" className="help-category-title">
            Show me how
          </h3>
          {tours.length === 0 ? (
            <p className="small muted m-0">No guide matches. Try another word.</p>
          ) : (
            <ul className="help-tours">
              {tours.map((tour) => {
                // Nothing loaded yet: the guide loads the samples first, so it always runs.
                const withSamples = tour.needsData && reportCount === 0
                return (
                  <li key={tour.id}>
                    <button
                      type="button"
                      className="help-tour"
                      aria-label={`Show me: ${tour.title}`}
                      onClick={() => {
                        onClose()
                        onStartTour(tour)
                      }}
                    >
                      <Icon name="pointer" size={14} />
                      <span>
                        <span className="help-page-name">{tour.title}</span>
                        <span className="help-page-text">
                          {tour.summary}
                          {withSamples && ' Loads the samples first.'}
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>

      <div className="help-dialog-footer">
        <Button
          tone="quiet"
          size="small"
          icon="listChecks"
          label="Getting started"
          onClick={() => {
            onClose()
            onOpenGuide()
          }}
        />
      </div>
    </Dialog>
  )
}
